import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  CloudArrowUpIcon,
  SparklesIcon,
  ClipboardDocumentListIcon,
  // DocumentTextIcon,  // Hidden for now
  // PlusCircleIcon,    // Hidden for now
  PhotoIcon
} from '@heroicons/react/24/outline';
import Button from '../components/common/Button';
import BetSlipCard from '../components/betslips/BetSlipCard';
import ManualBetSlipModal from '../components/betslips/ManualBetSlipModal';
import TextParseBetSlipModal from '../components/betslips/TextParseBetSlipModal';
import UploadBetSlipModal from '../components/betslips/UploadBetSlipModal';
import BetSlipDetailModal from '../components/betslips/BetSlipDetailModal';
import BettingAnalyticsDashboard from '../components/betslips/BettingAnalyticsDashboard';
import PremiumGateModal from '../components/common/PremiumGateModal';
import betslipService from '../services/betslipService';
import { useAuth } from '../contexts/AuthContext';

export default function BetSlips() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [betslips, setBetslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [isTextParseModalOpen, setIsTextParseModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedBetslip, setSelectedBetslip] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [activeTab, setActiveTab] = useState('bets'); // 'bets' or 'analytics'

  // Check if user is premium
  const isPremium = userProfile?.subscription_tier === 'premium' || userProfile?.subscription_tier === 'pro';

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { state: { from: '/betslips' } });
    }
  }, [currentUser, navigate]);

  const loadStats = useCallback(async () => {
    if (!isPremium) return;
    try {
      const data = await betslipService.getUserStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [isPremium]);

  const loadBetslips = useCallback(async () => {
    if (!isPremium) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await betslipService.getBetslips(statusFilter, 50);
      setBetslips(data);
    } catch (error) {
      console.error('Error loading betslips:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, isPremium]);

  // Load betslips and stats
  useEffect(() => {
    loadBetslips();
    loadStats();
  }, [statusFilter, loadBetslips, loadStats]);

  const handleBetslipCreated = (newBetslip) => {
    setBetslips([newBetslip, ...betslips]);
    loadStats();
  };

  const handleBetslipClick = (betslip) => {
    setSelectedBetslip(betslip);
    setIsDetailModalOpen(true);
  };

  // Handle when a betslip is updated (e.g., manually completed)
  const handleBetslipUpdate = (updatedBetslip) => {
    // Update the betslip in the list
    setBetslips((prev) =>
      prev.map((b) => (b.id === updatedBetslip.id ? updatedBetslip : b))
    );
    // Refresh stats to reflect the changes
    loadStats();
  };

  // Calculate stats from betslips data
  const calculateDisplayStats = () => {
    if (!stats || !stats.betslips) {
      return { winRate: 0, roi: 0, totalProfit: 0 };
    }

    const { betslips: betslipStats } = stats;
    const winRate = betslipStats.total > 0
      ? ((betslipStats.won / betslipStats.total) * 100).toFixed(1)
      : 0;

    const roi = betslipStats.total_staked > 0
      ? (((betslipStats.total_payout - betslipStats.total_staked) / betslipStats.total_staked) * 100).toFixed(1)
      : 0;

    const totalProfit = betslipStats.net_profit || 0;

    return { winRate, roi, totalProfit };
  };

  const displayStats = calculateDisplayStats();

  // Premium features list for the gate modal
  const premiumFeatures = [
    "Upload bet slips from any sportsbook app (DraftKings, FanDuel, BetMGM, and more)",
    "AI-powered OCR extracts bet details automatically",
    "Track all your bets in one place across multiple apps",
    "Detailed analytics: win rate, ROI, and profit/loss tracking",
    "Compare your picks against our AI predictions",
    "Identify your winning patterns and betting trends",
    "Export your betting history for tax purposes"
  ];

  return (
    <div className="relative min-h-[calc(100vh-8rem)] space-y-6">
      {/* Premium Gate Modal - show for non-premium users */}
      <PremiumGateModal
        isOpen={!isPremium}
        onClose={() => navigate(-1)}
        title="Bet Slip Tracking"
        description="Upload your bet slips from multiple sportsbook apps to track your winnings, analyze your performance, and improve your betting strategy."
        features={premiumFeatures}
      />

      {/* Header with Tabs */}
      <div className="glass-card p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gradient">Bet Slips</h1>
            <p className="mt-2 text-text-secondary">
              Track your bets across all sportsbooks, analyze ROI, and improve your strategy
            </p>
          </div>

          {/* Entry Options */}
          {isPremium && (
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setIsUploadModalOpen(true)}
              >
                <PhotoIcon className="w-4 h-4 mr-1" />
                Upload
              </Button>
              {/* Paste and Manual hidden for now
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsTextParseModalOpen(true)}
              >
                <DocumentTextIcon className="w-4 h-4 mr-1" />
                Paste
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsManualModalOpen(true)}
              >
                <PlusCircleIcon className="w-4 h-4 mr-1" />
                Manual
              </Button>
              */}
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        {isPremium && (
          <div className="flex gap-1 mt-6 border-b border-border">
            <button
              onClick={() => setActiveTab('bets')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === 'bets'
                  ? 'text-accent-blue border-accent-blue'
                  : 'text-text-secondary border-transparent hover:text-text-primary'
              }`}
            >
              <ClipboardDocumentListIcon className="w-4 h-4 inline mr-2" />
              My Bets
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === 'analytics'
                  ? 'text-accent-blue border-accent-blue'
                  : 'text-text-secondary border-transparent hover:text-text-primary'
              }`}
            >
              <ChartBarIcon className="w-4 h-4 inline mr-2" />
              Analytics
            </button>
          </div>
        )}
      </div>

      {/* Feature Highlights - Only show on bets tab or when not premium */}
      {(!isPremium || activeTab === 'bets') && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="glass-card-hover p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-accent-blue/20">
                <CloudArrowUpIcon className="w-6 h-6 text-accent-blue" />
              </div>
              <h3 className="font-semibold text-text-primary">Multi-App Upload</h3>
            </div>
            <p className="text-sm text-text-secondary">
              Import bet slips from DraftKings, FanDuel, BetMGM, Caesars, and all major sportsbooks
            </p>
          </div>

          <div className="glass-card-hover p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <SparklesIcon className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="font-semibold text-text-primary">AI-Powered OCR</h3>
            </div>
            <p className="text-sm text-text-secondary">
              Our AI automatically extracts bet details from screenshots - teams, odds, stakes, and more
            </p>
          </div>

          <div className="glass-card-hover p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-accent-green/20">
                <ChartBarIcon className="w-6 h-6 text-accent-green" />
              </div>
              <h3 className="font-semibold text-text-primary">Performance Analytics</h3>
            </div>
            <p className="text-sm text-text-secondary">
              Track win rates, ROI, and profit/loss across all your bets with detailed breakdowns
            </p>
          </div>

          <div className="glass-card-hover p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-yellow-500/20">
                <ArrowTrendingUpIcon className="w-6 h-6 text-yellow-400" />
              </div>
              <h3 className="font-semibold text-text-primary">Trend Analysis</h3>
            </div>
            <p className="text-sm text-text-secondary">
              Discover your winning patterns - which sports, bet types, and strategies work best for you
            </p>
          </div>
        </div>
      )}


      {/* Premium Content */}
      {isPremium && (
        <>
          {/* My Bets Tab */}
          {activeTab === 'bets' && (
            <>
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="glass-card-hover p-6">
                  <h3 className="text-sm font-medium text-text-secondary mb-2">Win Rate</h3>
                  <p className={`text-3xl font-bold ${displayStats.winRate > 50 ? 'text-accent-green' : 'text-accent-red'}`}>
                    {displayStats.winRate}%
                  </p>
                </div>
                <div className="glass-card-hover p-6">
                  <h3 className="text-sm font-medium text-text-secondary mb-2">ROI</h3>
                  <p className={`text-3xl font-bold ${displayStats.roi > 0 ? 'text-accent-green' : displayStats.roi < 0 ? 'text-accent-red' : 'text-accent-blue'}`}>
                    {displayStats.roi}%
                  </p>
                </div>
                <div className="glass-card-hover p-6">
                  <h3 className="text-sm font-medium text-text-secondary mb-2">Total Profit</h3>
                  <p className={`text-3xl font-bold ${displayStats.totalProfit > 0 ? 'text-accent-green' : displayStats.totalProfit < 0 ? 'text-accent-red' : 'text-text-primary'}`}>
                    ${displayStats.totalProfit.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Filters */}
              <div className="glass-card p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-text-secondary">Filter:</span>
                  {[
                    { value: null, label: 'All' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'won', label: 'Won' },
                    { value: 'lost', label: 'Lost' },
                    { value: 'pushed', label: 'Pushed' }
                  ].map((filter) => (
                    <button
                      key={filter.label}
                      onClick={() => setStatusFilter(filter.value)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                        statusFilter === filter.value
                          ? 'bg-accent-blue text-bg-primary'
                          : 'bg-bg-card text-text-primary hover:bg-bg-card-hover'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Bet History */}
              <div className="glass-card p-6">
                <h2 className="text-xl font-bold mb-4">
                  {statusFilter ? `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Bets` : 'All Bets'}
                  {betslips.length > 0 && ` (${betslips.length})`}
                </h2>

                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="glass-card-hover p-4">
                        <div className="animate-pulse">
                          <div className="h-4 bg-border rounded w-1/4 mb-2"></div>
                          <div className="h-6 bg-border rounded w-3/4"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : betslips.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📱</div>
                    <p className="text-text-secondary text-lg mb-4">No betslips found</p>
                    <p className="text-sm text-text-secondary mb-6">
                      Upload a screenshot to get started
                    </p>
                    <div className="flex justify-center gap-3">
                      <Button variant="primary" onClick={() => setIsUploadModalOpen(true)}>
                        <PhotoIcon className="w-4 h-4 mr-1" />
                        Upload Bet Slip
                      </Button>
                      {/* Paste and Manual hidden for now
                      <Button variant="secondary" onClick={() => setIsTextParseModalOpen(true)}>
                        <DocumentTextIcon className="w-4 h-4 mr-1" />
                        Paste
                      </Button>
                      <Button variant="secondary" onClick={() => setIsManualModalOpen(true)}>
                        <PlusCircleIcon className="w-4 h-4 mr-1" />
                        Manual
                      </Button>
                      */}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {betslips.map((betslip) => (
                      <BetSlipCard
                        key={betslip.id}
                        betslip={betslip}
                        onClick={() => handleBetslipClick(betslip)}
                        onUpdate={handleBetslipUpdate}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <BettingAnalyticsDashboard />
          )}
        </>
      )}

      {/* How It Works - Always visible */}
      <div className="glass-card p-6">
        <h2 className="text-xl font-bold mb-6 text-center">How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-accent-blue/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-xl font-bold text-accent-blue">1</span>
            </div>
            <h3 className="font-semibold mb-2">Screenshot Your Bet</h3>
            <p className="text-sm text-text-secondary">
              Take a screenshot of your bet slip from any sportsbook app
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-accent-blue/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-xl font-bold text-accent-blue">2</span>
            </div>
            <h3 className="font-semibold mb-2">Upload & Auto-Extract</h3>
            <p className="text-sm text-text-secondary">
              Our AI reads the image and extracts all bet details automatically
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-accent-blue/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-xl font-bold text-accent-blue">3</span>
            </div>
            <h3 className="font-semibold mb-2">Track & Analyze</h3>
            <p className="text-sm text-text-secondary">
              View your performance stats and discover your winning patterns
            </p>
          </div>
        </div>
      </div>

      {/* Manual Entry Modal */}
      <ManualBetSlipModal
        isOpen={isManualModalOpen}
        onClose={() => setIsManualModalOpen(false)}
        onSuccess={handleBetslipCreated}
      />

      {/* Text Parse Modal */}
      <TextParseBetSlipModal
        isOpen={isTextParseModalOpen}
        onClose={() => setIsTextParseModalOpen(false)}
        onSuccess={handleBetslipCreated}
      />

      {/* Upload Modal */}
      <UploadBetSlipModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={handleBetslipCreated}
      />

      {/* Detail Modal with Knewton Analysis */}
      <BetSlipDetailModal
        betslip={selectedBetslip}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedBetslip(null);
        }}
      />
    </div>
  );
}
